/**
 * What a respondent sees when a form registers people on an event (#919): the closed screens, the refusal parser and
 * the thank-you card's "where" line. `pages/PublicForm.jsx` is the only consumer, and it holds no copy of its own.
 *
 * The server's contract (E14, E40, E50):
 *   - the public GET carries `event_registration: {state, event}` when the form has a target (no key otherwise);
 *   - a refused submit is `409 {detail: {code: "registration_closed", reason, message, event}}`;
 *   - `event` is the E40 allowlist summary `{name, date, start_time, end_time, timezone, modality, location}`, where
 *     `location` is `{venue_name, city, country}` or null. It never carries the meeting link, and
 *     nothing here would render one (VR-B1).
 */
import { formatRegistrationWhen } from './formEventRegistration';

export const REGISTRATION_CLOSED_CODE = 'registration_closed';

const REFUSAL_REASONS = ['not_open', 'closed', 'full'];

/**
 * The 409 `registration_closed` refusal as `{reason, message, event}`, or null for anything else.
 *
 * This is the ONLY reader of that shape. The step route has two other 409s with a STRING detail (per-step submit
 * switched off, a stale step) and a client tells them apart by `detail.code`, so a string detail, a 422, a 5xx or a
 * network error all answer null. An unrecognised `reason` reads as `closed` (the screen that promises least).
 */
export function registrationRefusal(err) {
    const response = err?.response;
    if (response?.status !== 409) return null;
    const detail = response.data?.detail;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail) || detail.code !== REGISTRATION_CLOSED_CODE) return null;
    const event = detail.event;
    return {
        reason: REFUSAL_REASONS.includes(detail.reason) ? detail.reason : 'closed',
        message: typeof detail.message === 'string' ? detail.message : '',
        event: event && typeof event === 'object' && !Array.isArray(event) ? event : null,
    };
}

/**
 * The closed screen for each state that is not `open`. `icon` is a lucide export name (this file stays free of the icon
 * package so it can be unit-tested without it); `note` is the second body line, when there is one.
 */
export const REGISTRATION_SCREENS = {
    not_open: { icon: 'Clock', title: 'Registration opens soon', note: 'Check back later.' },
    closed: { icon: 'CalendarX', title: 'Registration is closed', note: '' },
    full: { icon: 'Users', title: 'This event is full', note: 'Registration has reached capacity.' },
};

/**
 * `{icon, title, summary, note}` for a state. `summary` is "{event} · {date}" (the date is the respondent-facing format,
 * `formatRegistrationWhen`), and is empty when there is no event summary (the event was deleted), so the screen omits
 * the line. A state this file does not know reads as `closed`.
 */
export function registrationScreen(state, event) {
    const screen = REGISTRATION_SCREENS[state] || REGISTRATION_SCREENS.closed;
    const name = typeof event?.name === 'string' ? event.name.trim() : '';
    const summary = event ? [name, formatRegistrationWhen(event)].filter(Boolean).join(' · ') : '';
    return { icon: screen.icon, title: screen.title, summary, note: screen.note };
}

// A venue field is typed text. If someone put a link in it, the "never a link" rule still holds.
const LOOKS_LIKE_URL = /^(?:[a-z][a-z0-9+.-]*:\/\/|www\.)/i;
const text = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Where the event is, for the thank-you card: "Online" for an online event; the venue, city and country for an in-person
 * one; the same plus " · Online" for a hybrid one. It is built from those three text fields and nothing else, so it can
 * never be (or contain) the event's meeting link. An in-person event with no location gives ''.
 */
export function registrationPlace(event) {
    const modality = text(event?.modality);
    if (modality === 'online') return 'Online';
    const location = event?.location && typeof event.location === 'object' ? event.location : {};
    const place = [text(location.venue_name), text(location.city), text(location.country)]
        .filter((part) => part && !LOOKS_LIKE_URL.test(part))
        .join(', ');
    if (modality === 'hybrid') return place ? `${place} · Online` : 'Online';
    return place;
}
